import type { ApiErrorBody, ApiSuccess, PageMeta } from '@otakotakdev/shared';

/** Base URL comes from Vite env — never hard-code API URLs in components. */
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: Record<string, unknown>;

  constructor(code: string, message: string, status: number, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export interface ListResult<T> {
  items: T[];
  meta: PageMeta;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      'x-request-id': crypto.randomUUID(),
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    if (!res.ok) throw new ApiError('HTTP_ERROR', `Request failed (${res.status})`, res.status);
    return (await res.text()) as unknown as T;
  }

  const body = (await res.json()) as ApiSuccess<T> | ApiErrorBody;
  if (!body.success) {
    throw new ApiError(body.error.code, body.error.message, res.status, body.error.details);
  }
  return body.data;
}

function qs(params: Record<string, unknown>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const api = {
  get: <T>(path: string, params?: Record<string, unknown>) =>
    request<T>(`${path}${qs(params ?? {})}`),
  post: <T>(path: string, data?: unknown) =>
    request<T>(path, { method: 'POST', body: data === undefined ? undefined : JSON.stringify(data) }),
  patch: <T>(path: string, data: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  list: async <T>(path: string, params?: Record<string, unknown>): Promise<ListResult<T>> => {
    const res = await fetch(`${API_BASE}${path}${qs(params ?? {})}`, {
      credentials: 'include',
      headers: { 'x-request-id': crypto.randomUUID() },
    });
    const body = (await res.json()) as ApiSuccess<T[]> & { meta?: PageMeta };
    if (!body.success) {
      const err = body as unknown as ApiErrorBody;
      throw new ApiError(err.error.code, err.error.message, res.status, err.error.details);
    }
    return { items: body.data, meta: body.meta ?? { page: 1, limit: body.data.length, total: body.data.length, totalPages: 1 } };
  },
};

export { API_BASE };
